import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Lock, ArrowRight, Check, ChevronDown, Star, X,
  Smartphone, Search, Calculator, ShoppingCart, Users, BarChart3,
  Mic, Bell, Shield, Zap, Clock, Headphones, Database,
  Code, Server, Globe, Layers, GitBranch, Terminal,
  FileText, Settings, Package, TrendingUp, MessageSquare,
  CheckCircle2, ArrowDown, Play
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";

const ACCESS_CODE = "BARRACA2026";

// Logo URLs
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_e0cac34e-e962-4104-a337-6bc642c1c62d/artifacts/n69ab7ik_Logo_Artificialmente_H_Blanco.png";
const LOGO_SEAL = "https://customer-assets.emergentagent.com/job_e0cac34e-e962-4104-a337-6bc642c1c62d/artifacts/svwt33t6_Logo_Artificialmente_Circulo.png";

// App screenshot for mockup
const APP_SCREENSHOT = "https://material-shop-5.preview.emergentagent.com";

// WhatsApp Icon
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Animated Section Component
function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Counter Animation
function CountUp({ end, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  
  useState(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);
  
  return <span ref={ref}>{isInView ? end : 0}{suffix}</span>;
}

// Access Code Screen
function AccessScreen({ onAccess }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toUpperCase() === ACCESS_CODE) {
      onAccess();
    } else {
      setError("Código incorrecto");
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm text-center relative z-10"
      >
        <img src={LOGO_DARK} alt="Artificialmente" className="h-8 mx-auto mb-16 opacity-60" />
        
        <motion.div
          animate={shake ? { x: [-10, 10, -10, 10, 0] } : {}}
          transition={{ duration: 0.4 }}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Propuesta Exclusiva</h1>
          <p className="text-gray-500 mb-8">Documento confidencial</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
              <Input
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(""); }}
                placeholder="• • • • • • • •"
                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-gray-600 text-center text-lg tracking-[0.5em] uppercase rounded-xl focus:border-blue-500 focus:ring-blue-500/20"
                autoFocus
              />
            </div>
            
            {error && <p className="text-red-400 text-sm">{error}</p>}
            
            <Button type="submit" className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-xl text-lg font-semibold">
              Acceder al documento
            </Button>
          </form>
        </motion.div>

        <Link to="/" className="inline-block mt-12 text-gray-600 hover:text-gray-400 text-sm">
          ← Volver al sitio
        </Link>
      </motion.div>
    </div>
  );
}

// Gallery with Lightbox Component
function GallerySection() {
  const [selectedImage, setSelectedImage] = useState(null);
  
  const mobileItem = {
    id: 0,
    title: "Versión Móvil",
    desc: "Optimizada para usar desde la obra",
    image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/186b33113c0e2e32609cec10ae67417cbed0b693b97018520c7572e2b8119672.png",
    isMobile: true
  };
  
  const galleryItems = [
    {
      id: 1,
      title: "Página Principal",
      desc: "Hero, categorías y productos destacados",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/0cd6c16511f9b9d4999ef59929728b444bd038f44c9d250e4ee3260e92bc28e9.png",
    },
    {
      id: 2,
      title: "Catálogo",
      desc: "Filtros y búsqueda",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/aca59282be01cd0e5a276621fc396972e0e272c76c855f1d13e1fedc0df8336a.png",
    },
    {
      id: 3,
      title: "Calculadora",
      desc: "Cálculo de materiales",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/8b03608c0e915b8f539aba3885a5512fad962f086beee163fdb0b74c6300377a.png",
    },
    {
      id: 4,
      title: "Carrito",
      desc: "Pedido o cotización",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/a096558080a40f89059bb0bd2e6aeb648f15c705073aaaf61039f8b3e17b06dd.png",
    },
    {
      id: 5,
      title: "Comparador",
      desc: "Hasta 4 productos",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/51fab4fdc7f903acf0531760c0fab615a258eda17e8e8ed00b101371aab1a0ca.png",
    },
    {
      id: 6,
      title: "Panel Admin",
      desc: "Gestión completa",
      image: "https://static.prod-images.emergentagent.com/jobs/e0cac34e-e962-4104-a337-6bc642c1c62d/images/122b225a45940ba30579c58dddd6f62e06f9696e1f6256e73635ccc054ef29dd.png",
    },
  ];

  const allItems = [mobileItem, ...galleryItems];

  return (
    <>
      {/* Mobile Hero - Centered at top, no background */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => setSelectedImage(mobileItem)}
        className="group relative mx-auto mb-8 block"
      >
        <img 
          src={mobileItem.image} 
          alt={mobileItem.title}
          className="h-56 md:h-72 w-auto mx-auto drop-shadow-2xl group-hover:scale-105 transition-transform"
        />
        <div className="text-center mt-4">
          <h4 className="font-semibold text-white">{mobileItem.title}</h4>
          <p className="text-gray-400 text-sm">{mobileItem.desc}</p>
        </div>
      </motion.button>

      {/* Rest of Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
        {galleryItems.map((item, idx) => (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            onClick={() => setSelectedImage(item)}
            className="group relative bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all hover:scale-[1.02]"
          >
            <img 
              src={item.image} 
              alt={item.title}
              className="w-full h-20 md:h-28 object-cover object-top"
            />
            <div className="p-2 md:p-3">
              <h4 className="font-medium text-white text-xs md:text-sm">{item.title}</h4>
              <p className="text-gray-500 text-[10px] md:text-xs hidden md:block">{item.desc}</p>
            </div>
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 transition-colors flex items-center justify-center">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Feature badges */}
      <div className="flex flex-wrap justify-center gap-2 mt-6 md:mt-8">
        {["WhatsApp 24/7", "Carga rápida", "Búsqueda por voz"].map((item, i) => (
          <span key={i} className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-xs text-gray-400">
            ✓ {item}
          </span>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white/60 hover:text-white flex items-center gap-2 text-sm"
            >
              <span>Cerrar</span>
              <X className="w-5 h-5" />
            </button>
            
            {/* Image */}
            <div className={`bg-gray-900 rounded-2xl overflow-hidden ${selectedImage.isMobile ? 'flex justify-center p-8' : ''}`}>
              <img 
                src={selectedImage.image} 
                alt={selectedImage.title}
                className={`${selectedImage.isMobile ? 'max-h-[70vh] w-auto rounded-xl' : 'w-full max-h-[75vh] object-contain'}`}
              />
            </div>
            
            {/* Caption */}
            <div className="mt-4 text-center">
              <h3 className="text-white font-semibold text-lg">{selectedImage.title}</h3>
              <p className="text-gray-400 text-sm">{selectedImage.desc}</p>
            </div>

            {/* Navigation arrows */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-4 pointer-events-none">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIdx = allItems.findIndex(i => i.id === selectedImage.id);
                  const prevIdx = currentIdx > 0 ? currentIdx - 1 : allItems.length - 1;
                  setSelectedImage(allItems[prevIdx]);
                }}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white pointer-events-auto transition-colors"
              >
                <ChevronDown className="w-5 h-5 rotate-90" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIdx = allItems.findIndex(i => i.id === selectedImage.id);
                  const nextIdx = currentIdx < allItems.length - 1 ? currentIdx + 1 : 0;
                  setSelectedImage(allItems[nextIdx]);
                }}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white pointer-events-auto transition-colors"
              >
                <ChevronDown className="w-5 h-5 -rotate-90" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

// Main Proposal Landing
function ProposalLanding() {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Main features - clean list
  const mainFeatures = [
    { icon: ShoppingCart, title: "Catálogo completo", desc: "Todos tus productos organizados por categorías" },
    { icon: Search, title: "Búsqueda inteligente", desc: "Encuentra productos al instante" },
    { icon: Mic, title: "Búsqueda por voz", desc: "Hablás y encontrás, sin escribir" },
    { icon: Calculator, title: "Calculadora", desc: "Cuántas cajas, bolsas o baldes necesita" },
    { icon: Layers, title: "Comparador", desc: "Compará hasta 4 productos lado a lado" },
    { icon: FileText, title: "Pedidos y cotizaciones", desc: "El cliente elige si compra o pide precio" },
    { icon: Settings, title: "Panel Admin", desc: "Gestioná todo desde un solo lugar" },
    { icon: Database, title: "Integración", desc: "Conectado con tu base de datos" },
    { icon: Bell, title: "Notificaciones", desc: "Email y WhatsApp automáticos" },
    { icon: Users, title: "Gestión de clientes", desc: "Registro, aprobación y gestión" },
    { icon: Shield, title: "Seguridad", desc: "Datos protegidos y backups diarios" },
    { icon: Smartphone, title: "Mobile first", desc: "Perfecta para usar desde la obra" },
  ];

  // Work breakdown - what we do
  const workBreakdown = [
    {
      phase: "Diseño UX/UI",
      items: ["Wireframes de todas las pantallas", "Diseño visual mobile-first", "Prototipos interactivos", "Revisiones y ajustes"]
    },
    {
      phase: "Frontend",
      items: ["15+ pantallas desarrolladas", "Diseño responsive (cel/tablet/PC)", "Animaciones y transiciones", "Optimización de velocidad"]
    },
    {
      phase: "Backend",
      items: ["API completa (+40 endpoints)", "Base de datos estructurada", "Sistema de autenticación", "Lógica de negocio"]
    },
    {
      phase: "Integraciones",
      items: ["Conexión con tu sistema", "Sincronización de productos", "Emails transaccionales", "WhatsApp notifications"]
    },
    {
      phase: "Testing",
      items: ["Pruebas funcionales", "Testing en dispositivos", "Corrección de errores", "Optimización final"]
    },
    {
      phase: "Deploy",
      items: ["Servidores en la nube", "Dominio y SSL", "Backups automáticos", "Capacitación"]
    },
  ];

  // AI Agent features
  const aiFeatures = [
    "Atiende consultas 24 horas, 7 días",
    "Responde preguntas sobre productos",
    "Conoce todo tu catálogo y precios",
    "Ayuda a encontrar lo que buscan",
    "Guía en el proceso de cotización",
    "Integrado con WhatsApp Business",
    "Habla como un vendedor de la barraca",
    "Aprende y mejora con el tiempo",
    "Escala a humano cuando es necesario",
    "Reportes de consultas frecuentes",
  ];

  // Tech stack
  const techStack = [
    { name: "React 19", icon: "⚛️" },
    { name: "Python", icon: "🐍" },
    { name: "MongoDB", icon: "🍃" },
    { name: "Google Cloud", icon: "☁️" },
    { name: "OpenAI", icon: "🤖" },
    { name: "Meta API", icon: "💬" },
    { name: "WhatsApp Business", icon: "📱" },
  ];

  // Guarantees
  const guarantees = [
    { icon: Shield, title: "30 días de garantía", desc: "Corrección gratuita de cualquier error de desarrollo" },
    { icon: Clock, title: "99.5% uptime", desc: "Garantía de disponibilidad del servicio" },
    { icon: Headphones, title: "Soporte 24hs", desc: "Respuesta en menos de 24 horas hábiles" },
    { icon: Database, title: "Backups diarios", desc: "Tus datos siempre respaldados y seguros" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      
      {/* Hero Section */}
      <section className="min-h-screen relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[120px]" />
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.02) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Nav */}
          <nav className="p-6 flex justify-between items-center">
            <img src={LOGO_DARK} alt="Artificialmente" className="h-6 md:h-8 opacity-70" />
            <a 
              href="https://wa.me/59899262722" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2 rounded-full text-sm transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4 text-green-400" />
              <span className="hidden sm:inline">Contactar</span>
            </a>
          </nav>

          {/* Hero Content */}
          <div className="flex-1 flex items-center justify-center px-6 py-12">
            <div className="max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-4 py-2 rounded-full text-blue-400 text-sm mb-8">
                  <Star className="w-4 h-4" />
                  Propuesta exclusiva
                </div>
                
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
                  Plataforma<br />
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    E-commerce
                  </span>
                </h1>
                
                <p className="text-xl md:text-2xl text-gray-400 mb-4">
                  Desarrollo integral para
                </p>
                <p className="text-3xl md:text-4xl font-bold text-white mb-12">
                  Barraca Maldonado
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => scrollToSection('funcionalidades')}
                    className="h-14 px-8 bg-white text-black hover:bg-gray-200 rounded-full text-lg font-semibold"
                  >
                    Ver todo lo que incluye
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    onClick={() => scrollToSection('inversion')}
                    variant="outline"
                    className="h-14 px-8 border-white/20 text-white hover:bg-white/10 rounded-full text-lg"
                  >
                    Ver inversión
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="pb-8 flex justify-center"
          >
            <button 
              onClick={() => scrollToSection('preview')}
              className="text-gray-600 hover:text-white transition-colors flex flex-col items-center gap-2"
            >
              <span className="text-xs uppercase tracking-widest">Scroll</span>
              <ChevronDown className="w-6 h-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Preview Section - Compact Gallery with Lightbox */}
      <section id="preview" className="py-16 md:py-20 px-4 md:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 to-transparent" />
        
        <div className="max-w-5xl mx-auto relative">
          <AnimatedSection>
            <div className="text-center mb-8 md:mb-10">
              <p className="text-blue-400 font-medium mb-2 text-xs md:text-sm uppercase tracking-widest">Vista previa</p>
              <h2 className="text-2xl md:text-4xl font-bold mb-3">
                Así va a quedar tu tienda
              </h2>
              <p className="text-gray-400 text-sm md:text-base max-w-md mx-auto">
                Tocá cualquier imagen para verla en grande
              </p>
            </div>
          </AnimatedSection>

          <GallerySection />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "60+", label: "Funcionalidades" },
              { number: "6", label: "Semanas de desarrollo" },
              { number: "24/7", label: "Soporte incluido" },
              { number: "99.5%", label: "Uptime garantizado" },
            ].map((stat, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    {stat.number}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Clean Grid */}
      <section id="funcionalidades" className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-12">
              <p className="text-blue-400 font-medium mb-2 text-xs md:text-sm uppercase tracking-widest">Funcionalidades</p>
              <h2 className="text-2xl md:text-4xl font-bold mb-3">
                Todo lo que incluye
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {mainFeatures.map((feature, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.05}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 hover:bg-white/10 transition-colors h-full">
                  <feature.icon className="w-6 h-6 md:w-8 md:h-8 text-blue-400 mb-3" />
                  <h4 className="font-semibold text-white text-sm md:text-base mb-1">{feature.title}</h4>
                  <p className="text-gray-500 text-xs md:text-sm">{feature.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Work Breakdown Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-12">
              <p className="text-blue-400 font-medium mb-2 text-xs md:text-sm uppercase tracking-widest">El trabajo</p>
              <h2 className="text-2xl md:text-4xl font-bold mb-3">
                Lo que hacemos
              </h2>
              <p className="text-gray-400 text-sm md:text-base max-w-lg mx-auto">
                Detrás de cada funcionalidad hay horas de diseño, desarrollo y testing.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {workBreakdown.map((phase, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.1}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                      {idx + 1}
                    </div>
                    <h4 className="font-semibold text-white text-sm md:text-base">{phase.phase}</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {phase.items.map((item, i) => (
                      <li key={i} className="text-gray-400 text-xs md:text-sm flex items-start gap-2">
                        <span className="text-blue-400 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Tech Stack - Compact */}
          <AnimatedSection delay={0.3}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {techStack.map((tech, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-full px-4 py-2 flex items-center gap-2">
                  <span>{tech.icon}</span>
                  <span className="text-white text-sm font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Landing Provisoria - Bonus */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center shrink-0">
                  <Zap className="w-6 h-6 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-2">Bonus: Sitio provisorio en 24 horas</h3>
                  <p className="text-gray-400 text-sm">
                    Mientras desarrollamos la plataforma completa, te armamos en un día una landing con cuenta regresiva, 
                    datos de contacto, ubicación y toda la info básica que necesites mostrar. Así no perdés presencia online ni un minuto.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* AI Agent Section - Special */}
      <section className="py-16 md:py-20 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-purple-900/10 to-transparent" />
        
        <div className="max-w-5xl mx-auto relative">
          <AnimatedSection>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 px-4 py-2 rounded-full text-purple-400 text-sm mb-4">
                <Zap className="w-4 h-4" />
                Opcional - Se cotiza aparte
              </div>
              <h2 className="text-2xl md:text-4xl font-bold mb-3">
                Asistente de Inteligencia Artificial
              </h2>
              <p className="text-gray-400 text-sm md:text-base max-w-lg mx-auto">
                Un agente virtual entrenado específicamente para tu negocio que atiende clientes las 24 horas.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 items-center">
            {/* AI Features List */}
            <AnimatedSection delay={0.1}>
              <div className="bg-white/5 border border-purple-500/20 rounded-2xl p-6 md:p-8">
                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  ¿Qué puede hacer?
                </h3>
                <ul className="space-y-3">
                  {aiFeatures.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>

            {/* AI Work Breakdown */}
            <AnimatedSection delay={0.2}>
              <div className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Code className="w-4 h-4 text-purple-400" />
                    Desarrollo
                  </h4>
                  <ul className="space-y-1.5 text-gray-400 text-sm">
                    <li>• Arquitectura del agente conversacional</li>
                    <li>• Integración con modelos de IA</li>
                    <li>• Conexión con base de datos de productos</li>
                    <li>• Sistema de contexto y memoria</li>
                  </ul>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-purple-400" />
                    Entrenamiento
                  </h4>
                  <ul className="space-y-1.5 text-gray-400 text-sm">
                    <li>• Personalización del tono y estilo</li>
                    <li>• Carga de información del negocio</li>
                    <li>• Definición de flujos de conversación</li>
                    <li>• Pruebas y ajustes iterativos</li>
                  </ul>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-purple-400" />
                    Integración
                  </h4>
                  <ul className="space-y-1.5 text-gray-400 text-sm">
                    <li>• Conexión con WhatsApp Business API</li>
                    <li>• Widget de chat en la web</li>
                    <li>• Panel de supervisión de conversaciones</li>
                    <li>• Reportes y métricas de uso</li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* AI Note */}
          <AnimatedSection delay={0.3}>
            <div className="mt-8 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 text-center">
              <p className="text-purple-300 text-sm">
                La implementación del agente de IA se cotiza por separado e incluye desarrollo, entrenamiento, integración y mantenimiento mensual.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Guarantees - Compact */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {guarantees.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <item.icon className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <h4 className="font-medium text-white text-sm mb-1">{item.title}</h4>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          <AnimatedSection delay={0.3}>
            <div className="text-center mt-6 text-gray-500 text-sm">
              Tiempo estimado de desarrollo: <span className="text-white font-medium">6-8 semanas</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="inversion" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <p className="text-blue-400 font-medium mb-2 text-sm uppercase tracking-widest">Inversión</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Dos opciones para elegir
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                Todo lo de arriba incluido. La única diferencia es si querés sumar un asistente de inteligencia artificial.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Option 1 */}
            <AnimatedSection delay={0.1}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 h-full flex flex-col">
                <div className="mb-6">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Opción 1</span>
                  <h3 className="text-2xl font-bold mt-1">Web Completa</h3>
                  <p className="text-gray-500 text-sm mt-1">Todo lo detallado en esta propuesta</p>
                </div>
                
                <div className="mb-4">
                  <p className="text-4xl font-bold text-gray-500">– – –</p>
                  <p className="text-gray-500">pago único</p>
                </div>

                <div className="mb-6 pb-6 border-b border-white/10">
                  <p className="text-3xl font-bold text-gray-500">– – –<span className="text-lg font-normal text-gray-600">/mes</span></p>
                  <p className="text-gray-500 text-sm">todo incluido</p>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {[
                    "Todas las funcionalidades",
                    "Integración con tu sistema",
                    "Sitio provisorio en 24hs",
                    "Hosting y mantenimiento",
                    "Soporte técnico continuo",
                    "Consultoría y asesoramiento",
                    "Ayuda con integración de sistemas"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => window.open('https://wa.me/59899262722?text=Hola!%20Me%20interesa%20la%20Opción%201%20(Web%20Completa)%20para%20Barraca%20Maldonado', '_blank')}
                  variant="outline" 
                  className="w-full h-14 border-white/20 text-white hover:bg-white/10 rounded-xl text-lg"
                >
                  Consultar
                </Button>
              </div>
            </AnimatedSection>

            {/* Option 2 */}
            <AnimatedSection delay={0.2}>
              <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-8 h-full flex flex-col relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    RECOMENDADO
                  </span>
                </div>

                <div className="mb-6">
                  <span className="text-xs font-medium text-blue-400 uppercase tracking-widest">Opción 2</span>
                  <h3 className="text-2xl font-bold mt-1">Web + Asistente IA</h3>
                  <p className="text-gray-400 text-sm mt-1">Todo lo anterior + inteligencia artificial</p>
                </div>
                
                <div className="mb-4">
                  <p className="text-4xl font-bold text-gray-400">– – –</p>
                  <p className="text-gray-400">pago único</p>
                </div>

                <div className="mb-6 pb-6 border-b border-white/20">
                  <p className="text-3xl font-bold text-gray-400">– – –<span className="text-lg font-normal text-gray-500">/mes</span></p>
                  <p className="text-gray-400 text-sm">todo incluido + servicios de IA</p>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {[
                    "Todo lo de la Opción 1",
                    "Asistente de IA entrenado",
                    "Responde consultas automáticamente",
                    "Integración con WhatsApp Business",
                    "Aprende de tu catálogo",
                    "Atiende clientes 24/7"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-200">
                      <Check className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => window.open('https://wa.me/59899262722?text=Hola!%20Me%20interesa%20la%20Opción%202%20(Web%20+%20IA)%20para%20Barraca%20Maldonado', '_blank')}
                  className="w-full h-14 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 rounded-xl text-lg font-semibold"
                >
                  Consultar
                </Button>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={0.3}>
            <div className="text-center mt-8 text-gray-500 text-sm max-w-xl mx-auto">
              <p>Forma de pago: 50% al inicio, 50% contra entrega</p>
              <p className="mt-1">Compromiso mínimo de 12 meses para el servicio mensual</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Monthly includes */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-white/5">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <h3 className="text-lg md:text-xl font-bold text-center mb-2">¿Qué incluye la mensualidad?</h3>
            <p className="text-gray-500 text-sm text-center mb-6">Infraestructura, soporte, consultoría y más</p>
          </AnimatedSection>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Server, label: "Hosting en la nube" },
              { icon: Database, label: "Backups diarios" },
              { icon: Shield, label: "Seguridad y SSL" },
              { icon: Zap, label: "Velocidad optimizada" },
              { icon: Headphones, label: "Soporte técnico" },
              { icon: MessageSquare, label: "Consultoría continua" },
              { icon: Settings, label: "Mantenimiento" },
              { icon: Users, label: "Ayuda con integraciones" },
            ].map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.05}>
                <div className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
                  <item.icon className="w-4 h-4 text-blue-400 shrink-0" />
                  <span className="text-xs md:text-sm text-gray-300">{item.label}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={0.3}>
            <p className="text-center text-gray-500 text-xs md:text-sm mt-6 max-w-2xl mx-auto">
              La mensualidad incluye todo lo necesario: hosting, mantenimiento, actualizaciones de seguridad, 
              soporte técnico por WhatsApp, consultoría tecnológica y asesoramiento para la integración con tus sistemas actuales o futuros.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[100px]" />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              ¿Arrancamos?
            </h2>
            <p className="text-gray-400 mb-10 text-lg">
              Coordinemos una llamada para resolver cualquier duda y definir los próximos pasos.
            </p>
            <a 
              href="https://wa.me/59899262722?text=Hola!%20Vi%20la%20propuesta%20completa%20para%20Barraca%20Maldonado.%20Quiero%20coordinar%20una%20llamada."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-400 text-white px-10 py-5 rounded-full text-xl font-semibold transition-colors shadow-lg shadow-green-500/30"
            >
              <WhatsAppIcon className="w-7 h-7" />
              Hablemos
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={LOGO_SEAL} alt="Artificialmente" className="h-12 opacity-40" />
          <div className="text-center md:text-right">
            <p className="text-gray-500 text-sm">Artificialmente · Inteligencia Empresarial</p>
            <p className="text-gray-600 text-xs mt-1">Documento confidencial · Marzo 2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Main Component
export default function Propuesta() {
  const [authenticated, setAuthenticated] = useState(false);

  if (!authenticated) {
    return <AccessScreen onAccess={() => setAuthenticated(true)} />;
  }

  return <ProposalLanding />;
}
